import { ChevronDown, Edit3, History, Layers, Library, Loader2, Music2, Plus, RotateCcw, Search, Trash2, X } from 'lucide-react'
import { Fragment, useState, type FormEvent, type MouseEvent as ReactMouseEvent, type ReactNode, type RefObject } from 'react'
import type { SunoPrompt, SunoPromptFilters, SunoPromptGroup, SunoPromptPool, SunoStylePromptHistoryItem } from '../../../api/suno'
import { Button, DataTableElement, DataTableSurface, Drawer, EmptyState, FormField, IconButton, MobileEntityList, Modal, ModalActions, Pill, RowActions, SearchField, SortDirectionIcon, TablePagination, TableSortButton } from '../../../components/ui'
import styles from '../../../pages/ToolsPage.module.css'

const cx = (...classes: Array<string | false | null | undefined>): string =>
  classes
    .filter(Boolean)
    .map((className) => styles[className as string] ?? className)
    .join(' ')

const shortPromptPreview = (value: string | null, fallback: string): string => {
  const trimmed = value?.trim()

  if (!trimmed) {
    return fallback
  }

  return trimmed.length > 180 ? `${trimmed.slice(0, 180).trim()}...` : trimmed
}

const formatHistoryDate = (value: string): string =>
  new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    month: 'short'
  }).format(new Date(value))

export type PromptPoolEditorState = {
  description: string
  name: string
  pool: SunoPromptPool
}

type PromptGroupFilterMenuProps = {
  detailsRef?: RefObject<HTMLDetailsElement | null> | undefined
  groups: SunoPromptGroup[]
  onChange: (groupId: string) => void
  selectedGroupId: string
}

export const PromptGroupFilterMenu = ({
  detailsRef,
  groups,
  onChange,
  selectedGroupId
}: PromptGroupFilterMenuProps) => {
  const selectedGroupName = groups.find((group) => group.id === selectedGroupId)?.name ?? 'All groups'

  const clearFilter = (event: ReactMouseEvent<HTMLButtonElement>): void => {
    event.preventDefault()
    event.stopPropagation()
    onChange('')
  }

  return (
    <details className={cx('toolbar-filter-menu', 'toolbar-group-filter', styles['prompt-group-filter'])} ref={detailsRef}>
      <summary>
        <Music2 size={15} />
        <span>{selectedGroupName}</span>
        {selectedGroupId && (
          <button
            aria-label="Clear group filter"
            className={cx('toolbar-filter-clear')}
            onClick={clearFilter}
            title="Clear group filter"
            type="button"
          >
            <X size={13} />
          </button>
        )}
        <ChevronDown size={15} />
      </summary>
      <div className={cx('toolbar-menu-panel')}>
        <button
          className={!selectedGroupId ? cx('toolbar-menu-option', 'active') : cx('toolbar-menu-option')}
          onClick={() => onChange('')}
          type="button"
        >
          All groups
        </button>
        {groups.map((group) => (
          <button
            className={selectedGroupId === group.id ? cx('toolbar-menu-option', 'active') : cx('toolbar-menu-option')}
            key={group.id}
            onClick={() => onChange(group.id)}
            type="button"
          >
            <span>{group.name}</span>
            <Pill>{group.prompt_count}</Pill>
          </button>
        ))}
        {groups.length === 0 && <span className="muted">No prompt groups created.</span>}
      </div>
    </details>
  )
}

type PromptLibraryControlsProps = {
  detailsRef?: RefObject<HTMLDetailsElement | null>
  groups: SunoPromptGroup[]
  isLoading: boolean
  onClearSearch: () => void
  onGroupChange: (groupId: string) => void
  onSearchChange: (search: string) => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  search: string
  selectedGroupId: string
}

export const PromptLibraryDesktopControls = ({
  detailsRef,
  groups,
  isLoading,
  onClearSearch,
  onGroupChange,
  onSearchChange,
  onSubmit,
  search,
  selectedGroupId
}: PromptLibraryControlsProps) => {
  return (
    <form className={cx(styles['prompt-control-bar'], styles['prompt-desktop-controls'])} onSubmit={onSubmit}>
      <div className={cx('search-field', styles['prompt-search-field'])}>
        <Search size={16} />
        <input
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search saved prompts..."
          value={search}
        />
      </div>
      <PromptGroupFilterMenu
        detailsRef={detailsRef}
        groups={groups}
        onChange={onGroupChange}
        selectedGroupId={selectedGroupId}
      />
      <button
        aria-label="Search saved prompts"
        className={styles['prompt-search-icon-btn']}
        disabled={isLoading}
        title="Search"
        type="submit"
      >
        {isLoading ? <Loader2 className="tools-spin" size={16} /> : <Search size={16} />}
      </button>
      <Button className={cx(styles['prompt-search-btn-desktop'])} disabled={isLoading} type="submit">
        {isLoading ? <Loader2 className="tools-spin" size={16} /> : <Search size={16} />}
        Search
      </Button>
      {search && (
        <Button
          className={cx(styles['prompt-clear-btn-desktop'])}
          disabled={isLoading}
          onClick={onClearSearch}
        >
          <X size={16} /> Clear
        </Button>
      )}
    </form>
  )
}

export const PromptLibraryMobileControls = ({
  groups,
  isLoading,
  onGroupChange,
  onSearchChange,
  onSubmit,
  search,
  selectedGroupId
}: Omit<PromptLibraryControlsProps, 'detailsRef' | 'onClearSearch'>) => {
  return (
    <form className={styles['prompt-mobile-controls']} onSubmit={onSubmit}>
      <div className={styles['prompt-mobile-search-row']}>
        <SearchField
          className={styles['prompt-search-field'] ?? ''}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search saved prompts..."
          size="mobile"
          value={search}
        />
        <button
          aria-label="Search saved prompts"
          className={styles['prompt-search-icon-btn']}
          disabled={isLoading}
          title="Search"
          type="submit"
        >
          {isLoading ? <Loader2 className="tools-spin" size={16} /> : <Search size={16} />}
        </button>
      </div>
      <div className={styles['prompt-mobile-filter-row']}>
        <PromptGroupFilterMenu
          groups={groups}
          onChange={onGroupChange}
          selectedGroupId={selectedGroupId}
        />
      </div>
    </form>
  )
}

type PromptLibraryListProps = {
  error: string | null
  expandedPromptPoolAnchorPromptIds: Record<string, string | undefined>
  expandedPromptPoolIds: Set<string>
  isLoading: boolean
  items: SunoPrompt[]
  onNextPage: () => void
  onPageSizeChange: (pageSize: number) => void
  onPreviousPage: () => void
  onSort: (sortBy: NonNullable<SunoPromptFilters['sortBy']>) => void
  page: number
  pageSize: number
  poolDetailsById: Record<string, SunoPromptPool | undefined>
  poolErrorsById: Record<string, string | undefined>
  renderMobileCard: (prompt: SunoPrompt, options?: { isPoolVariant?: boolean, pool?: SunoPromptPool | undefined }) => ReactNode
  renderTableRow: (prompt: SunoPrompt, options?: { isCurrentPromptVariant?: boolean, isExpanded?: boolean, isPoolVariant?: boolean, pool?: SunoPromptPool | undefined }) => ReactNode
  sortBy: SunoPromptFilters['sortBy']
  sortDirection: SunoPromptFilters['sortDirection']
  toolbar: ReactNode
  total: number
}

const promptPageSizeOptions = [10, 25, 50, 100]

export const PromptLibraryList = ({
  error,
  expandedPromptPoolAnchorPromptIds,
  expandedPromptPoolIds,
  isLoading,
  items,
  onNextPage,
  onPageSizeChange,
  onPreviousPage,
  onSort,
  page,
  pageSize,
  poolDetailsById,
  poolErrorsById,
  renderMobileCard,
  renderTableRow,
  sortBy,
  sortDirection,
  toolbar,
  total
}: PromptLibraryListProps) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const getSortLabel = (targetSortBy: NonNullable<SunoPromptFilters['sortBy']>): string => {
    if ((sortBy ?? 'updated_at') !== targetSortBy) {
      return 'Not sorted.'
    }

    return `Sorted ${sortDirection === 'asc' ? 'ascending' : 'descending'}.`
  }

  const renderPromptPoolMobileVariants = (prompt: SunoPrompt): ReactNode => {
    const poolId = prompt.pool?.id
    const isExpanded = Boolean(poolId && expandedPromptPoolIds.has(poolId) && expandedPromptPoolAnchorPromptIds[poolId] === prompt.id)
    const pool = poolId ? poolDetailsById[poolId] : undefined
    const poolError = poolId ? poolErrorsById[poolId] : undefined

    return (
      <Fragment key={`mobile-fragment-${prompt.id}`}>
        {renderMobileCard(prompt)}
        {poolId && isExpanded && poolError && (
          <div className={styles['tools-error']}>{poolError}</div>
        )}
        {poolId && isExpanded && pool?.variants.map((variant) =>
          renderMobileCard(variant.prompt, {
            isPoolVariant: true,
            pool
          })
        )}
      </Fragment>
    )
  }

  const renderPromptTableRows = (prompt: SunoPrompt): ReactNode => {
    const poolId = prompt.pool?.id
    const isExpanded = Boolean(poolId && expandedPromptPoolIds.has(poolId) && expandedPromptPoolAnchorPromptIds[poolId] === prompt.id)
    const pool = poolId ? poolDetailsById[poolId] : undefined
    const poolError = poolId ? poolErrorsById[poolId] : undefined

    return (
      <Fragment key={prompt.id}>
        {renderTableRow(prompt, { isExpanded })}
        {poolId && isExpanded && (
          <>
            {poolError && (
              <tr className={styles['prompt-pool-sublist-header']}>
                <td colSpan={8}>
                  <div className={styles['tools-error']}>{poolError}</div>
                </td>
              </tr>
            )}
            {pool?.variants.map((variant) =>
              renderTableRow(variant.prompt, {
                isCurrentPromptVariant: variant.prompt.id === prompt.id,
                isPoolVariant: true,
                pool
              })
            )}
          </>
        )}
      </Fragment>
    )
  }

  return (
    <>
      <MobileEntityList
        className={styles['prompt-mobile-list'] ?? ''}
        toolbar={toolbar}
        empty={!isLoading && items.length === 0 ? <EmptyState message="No saved prompts yet." /> : null}
      >
        {error && <div className={styles['tools-error']}>{error}</div>}
        {isLoading && (
          <div className={cx(styles['tools-results-state'], styles['prompt-results-state'])}>
            <Loader2 className="tools-spin" size={24} />
            <span>Loading saved prompts...</span>
          </div>
        )}
        {!isLoading && items.map(renderPromptPoolMobileVariants)}
      </MobileEntityList>

      <DataTableSurface className={cx(styles['prompt-table-card'], styles['prompt-desktop-table-card'])} aria-label="Saved prompts">
        {error && <div className={styles['tools-error']}>{error}</div>}

        {isLoading && (
          <div className={cx(styles['tools-results-state'], styles['prompt-results-state'])}>
            <Loader2 className="tools-spin" size={24} />
            <span>Loading saved prompts...</span>
          </div>
        )}

        {!isLoading && items.length === 0 && (
          <div className={cx(styles['tools-results-state'], styles['prompt-results-state'])}>
            <Library size={24} />
            <span>No saved prompts yet.</span>
          </div>
        )}

        {!isLoading && items.length > 0 && (
          <>
            <div className={styles['prompt-table-scroll']}>
              <DataTableElement className={styles['prompt-table']}>
                <thead>
                  <tr>
                    <th className="col-icon"></th>
                    <th>
                      <TableSortButton
                        aria-label={`Sort by title. ${getSortLabel('title')}`}
                        onClick={() => onSort('title')}
                      >
                        <span>Title</span>
                        {(sortBy ?? 'updated_at') === 'title' && <SortDirectionIcon direction={sortDirection ?? 'desc'} />}
                      </TableSortButton>
                    </th>
                    <th>
                      <TableSortButton
                        aria-label={`Sort by groups. ${getSortLabel('groups')}`}
                        onClick={() => onSort('groups')}
                      >
                        <span>Groups</span>
                        {sortBy === 'groups' && <SortDirectionIcon direction={sortDirection ?? 'desc'} />}
                      </TableSortButton>
                    </th>
                    <th>Style prompt</th>
                    <th>Exclude</th>
                    <th>
                      <TableSortButton
                        aria-label={`Sort by linked tracks. ${getSortLabel('linked_track_count')}`}
                        onClick={() => onSort('linked_track_count')}
                      >
                        <span>Tracks</span>
                        {sortBy === 'linked_track_count' && <SortDirectionIcon direction={sortDirection ?? 'desc'} />}
                      </TableSortButton>
                    </th>
                    <th>
                      <TableSortButton
                        aria-label={`Sort by date. ${getSortLabel('updated_at')}`}
                        onClick={() => onSort('updated_at')}
                      >
                        <span>Updated</span>
                        {(sortBy ?? 'updated_at') === 'updated_at' && <SortDirectionIcon direction={sortDirection ?? 'desc'} />}
                      </TableSortButton>
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(renderPromptTableRows)}
                </tbody>
              </DataTableElement>
            </div>
            <TablePagination
              ariaLabel="Prompt pagination"
              currentPage={page}
              isNextDisabled={page >= totalPages}
              onNext={onNextPage}
              onPrevious={onPreviousPage}
              pageSize={{
                onChange: onPageSizeChange,
                options: promptPageSizeOptions,
                value: pageSize
              }}
              summary={total === 0
                ? 'No prompts'
                : `${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, total)} of ${total}`}
              totalPages={totalPages}
            />
          </>
        )}
      </DataTableSurface>
    </>
  )
}

type StylePromptHistoryDrawerProps = {
  deletingId: string | null
  error: string | null
  isLoading: boolean
  items: SunoStylePromptHistoryItem[]
  onClose: () => void
  onDelete: (item: SunoStylePromptHistoryItem) => void
  onOpen: (item: SunoStylePromptHistoryItem) => void
}

export const StylePromptHistoryDrawer = ({
  deletingId,
  error,
  isLoading,
  items,
  onClose,
  onDelete,
  onOpen
}: StylePromptHistoryDrawerProps) => {
  return (
    <Drawer
      ariaLabelledBy="style-history-title"
      closeLabel="Close history"
      onClose={onClose}
      subtitle="Recent generated style prompt results"
      title={<h2 id="style-history-title">History</h2>}
    >
      <div className={styles['tools-history-drawer-summary']}>
        <span>{isLoading ? 'Loading' : `${items.length}/50 saved`}</span>
      </div>

      {error && <div className={styles['tools-error']}>{error}</div>}

      {isLoading && (
        <div className={styles['tools-history-state']}>
          <Loader2 className="tools-spin" size={20} />
          <span>Loading history...</span>
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <div className={styles['tools-history-empty']}>
          <History size={22} />
          <span>Generated prompts will appear here.</span>
        </div>
      )}

      {!isLoading && items.length > 0 && (
        <ol className={styles['tools-history-list']}>
          {items.map((item) => (
            <li className={styles['tools-history-item']} key={item.id}>
              <button
                className={styles['tools-history-open']}
                onClick={() => onOpen(item)}
                type="button"
              >
                <span className={styles['tools-history-description']}>{item.description}</span>
                <span className={styles['tools-history-date']}>{formatHistoryDate(item.created_at)}</span>
              </button>
              <div className={styles['tools-history-actions']}>
                <span className={styles['tools-history-count']} title={`${item.result_json.length} variants`}>
                  <Layers size={14} />
                  {item.result_json.length}
                </span>
                <IconButton
                  icon={<RotateCcw size={15} />}
                  label={`Open history item from ${formatHistoryDate(item.created_at)}`}
                  onClick={() => onOpen(item)}
                  title="Open"
                />
                <IconButton
                  disabled={deletingId === item.id}
                  icon={deletingId === item.id ? <Loader2 className="tools-spin" size={15} /> : <Trash2 size={15} />}
                  label={`Delete history item from ${formatHistoryDate(item.created_at)}`}
                  onClick={() => onDelete(item)}
                  title="Delete"
                  variant="danger"
                />
              </div>
            </li>
          ))}
        </ol>
      )}
    </Drawer>
  )
}

type PromptLibraryPickerProps = {
  error: string | null
  isLoading: boolean
  items: SunoPrompt[]
  onClose: () => void
  onSearch: (search: string) => void
  onSearchChange: (search: string) => void
  onSelect: (prompt: SunoPrompt) => void
  search: string
  selectedId: string | null
}

export const PromptLibraryPicker = ({
  error,
  isLoading,
  items,
  onClose,
  onSearch,
  onSearchChange,
  onSelect,
  search,
  selectedId
}: PromptLibraryPickerProps) => {
  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    onSearch(search)
  }

  return (
    <Modal
      ariaLabelledBy="prompt-library-title"
      backdropClassName={cx(styles['prompt-library-backdrop'])}
      onClose={onClose}
      panelClassName={cx(styles['prompt-library-panel'])}
    >
      <div className="ui-modal-heading modal-heading">
        <div>
          <h2 id="prompt-library-title">Prompt Library</h2>
          <p>Select one saved prompt as the foundation for this music brief.</p>
        </div>
        <IconButton icon={<X size={17} />} label="Close prompt library" onClick={onClose} />
      </div>

      <form className={styles['prompt-library-search']} onSubmit={submit}>
        <div className={cx('search-field', styles['prompt-search-field'])}>
          <Search size={16} />
          <input
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search saved prompts..."
            value={search}
          />
        </div>
        <Button disabled={isLoading} icon={isLoading ? <Loader2 className="tools-spin" size={16} /> : <Search size={16} />} type="submit" variant="secondary">
          Search
        </Button>
      </form>

      {error && <div className={styles['tools-error']}>{error}</div>}

      {isLoading && (
        <div className={cx(styles['tools-results-state'], styles['prompt-library-state'])}>
          <Loader2 className="tools-spin" size={24} />
          <span>Loading prompt library...</span>
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <div className={cx(styles['tools-results-state'], styles['prompt-library-state'])}>
          <Library size={24} />
          <span>No saved prompts found.</span>
        </div>
      )}

      {!isLoading && items.length > 0 && (
        <ol className={styles['prompt-library-list']}>
          {items.map((prompt) => (
            <li className={styles['prompt-library-item']} key={prompt.id}>
              <button
                className={selectedId === prompt.id ? cx(styles['prompt-library-option'], 'selected') : cx(styles['prompt-library-option'])}
                onClick={() => onSelect(prompt)}
                type="button"
              >
                <span className={styles['prompt-library-option-title']}>
                  <strong>{prompt.title}</strong>
                  {prompt.description && <small>{prompt.description}</small>}
                </span>
                <span className={styles['prompt-library-preview']}>
                  <em>Style</em>
                  <code>{shortPromptPreview(prompt.style_prompt, 'No style prompt preview')}</code>
                </span>
                <span className={styles['prompt-library-preview']}>
                  <em>Exclude</em>
                  <code>{shortPromptPreview(prompt.exclude_prompt, 'No exclude prompt')}</code>
                </span>
              </button>
            </li>
          ))}
        </ol>
      )}
    </Modal>
  )
}

type PromptGroupsProps = {
  error: string | null
  groups: SunoPromptGroup[]
  isLoading: boolean
  onCreate: () => void
  onDelete: (group: SunoPromptGroup) => void
  onEdit: (group: SunoPromptGroup) => void
  onSearch: (search: string) => void
}

export const PromptGroups = ({
  error,
  groups,
  isLoading,
  onCreate,
  onDelete,
  onEdit,
  onSearch
}: PromptGroupsProps) => {
  const [search, setSearch] = useState('')

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    onSearch(search)
  }

  return (
    <>
      <form className="toolbar visual-toolbar prompt-groups-toolbar" onSubmit={submit}>
        <Button className="toolbar-upload group-create-button" mobileFullWidth onClick={onCreate} variant="primary">
          <Plus size={16} /> Create prompt group
        </Button>
        <div className="search-field">
          <Search size={16} />
          <input
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search prompt groups..."
            value={search}
          />
        </div>
        <Button className={cx(styles['prompt-search-btn-desktop'])} disabled={isLoading} mobileFullWidth type="submit">
          {isLoading ? <Loader2 className="tools-spin" size={16} /> : <Search size={16} />}
          Search
        </Button>
      </form>

      <DataTableSurface className={cx('audio-groups-table-card', styles['prompt-groups-table-card'])} fullWidth mobileStack mobileVariant="entityList">
        <div className={cx('table-title', 'table-title-row')}>
          <span>Prompt Groups</span>
          <Pill>{isLoading ? 'Loading' : `${groups.length} groups`}</Pill>
        </div>

        {error && <div className={styles['tools-error']}>{error}</div>}

        {isLoading && (
          <div className={cx(styles['tools-results-state'], styles['prompt-results-state'])}>
            <Loader2 className="tools-spin" size={24} />
            <span>Loading prompt groups...</span>
          </div>
        )}

        {!isLoading && groups.length === 0 && (
          <EmptyState message="No prompt groups match this search." />
        )}

        {!isLoading && groups.length > 0 && (
          <DataTableElement>
            <thead>
              <tr>
                <th>Name</th>
                <th>Prompts</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <tr key={group.id}>
                  <td className="group-title-cell" data-label="">
                    <span className="group-title-line">
                      <strong>{group.name}</strong>
                    </span>
                    {group.description && <span>{group.description}</span>}
                  </td>
                  <td className="group-secondary-cell" data-label="Prompts">
                    <Pill strong>{group.prompt_count}</Pill>
                  </td>
                  <td className="group-secondary-cell" data-label="Updated">
                    {new Date(group.updated_at).toLocaleString()}
                  </td>
                  <td className="group-actions-cell" data-label="Actions">
                    <RowActions className="group-inline-actions" mobileStack>
                      <IconButton
                        icon={<Edit3 size={15} />}
                        label={`Edit ${group.name}`}
                        onClick={() => onEdit(group)}
                        title="Edit"
                      />
                      <IconButton
                        icon={<Trash2 size={15} />}
                        label={`Delete ${group.name}`}
                        onClick={() => onDelete(group)}
                        title="Delete"
                        variant="danger"
                      />
                    </RowActions>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTableElement>
        )}
      </DataTableSurface>
    </>
  )
}

type PromptGroupDialogProps = {
  group: SunoPromptGroup
  isSaving: boolean
  onCancel: () => void
  onChange: (group: SunoPromptGroup) => void
  onSave: (group: SunoPromptGroup) => void
}

export const PromptGroupDialog = ({
  group,
  isSaving,
  onCancel,
  onChange,
  onSave
}: PromptGroupDialogProps) => {
  const nameValid = group.name.trim().length > 0

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    onSave(group)
  }

  return (
    <Modal
      ariaLabelledBy="prompt-group-dialog-title"
      isDismissDisabled={isSaving}
      mobilePresentation="sheet"
      onClose={onCancel}
      onSubmit={submit}
      title={<h2 id="prompt-group-dialog-title">{group.id ? 'Edit prompt group' : 'Create prompt group'}</h2>}
    >
      <FormField label="Name">
        <input
          className="form-input"
          maxLength={160}
          onChange={(event) => onChange({ ...group, name: event.target.value })}
          value={group.name}
        />
      </FormField>
      <FormField label="Description">
        <textarea
          className="form-input"
          maxLength={3000}
          onChange={(event) => onChange({ ...group, description: event.target.value })}
          value={group.description ?? ''}
        />
      </FormField>
      {group.id && (
        <p className="muted">
          Contains {group.prompt_count} prompt(s).
        </p>
      )}
      <ModalActions>
        <Button disabled={isSaving} onClick={onCancel} variant="secondary">Cancel</Button>
        <Button disabled={!nameValid || isSaving} type="submit" variant="primary">
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </ModalActions>
    </Modal>
  )
}

type PromptPoolDialogProps = {
  isSaving: boolean
  onCancel: () => void
  onChange: (value: PromptPoolEditorState) => void
  onSave: (value: PromptPoolEditorState) => void
  value: PromptPoolEditorState
}

export const PromptPoolDialog = ({
  isSaving,
  onCancel,
  onChange,
  onSave,
  value
}: PromptPoolDialogProps) => {
  const nameValid = value.name.trim().length > 0

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    onSave(value)
  }

  return (
    <Modal
      ariaLabelledBy="prompt-pool-dialog-title"
      isDismissDisabled={isSaving}
      mobilePresentation="sheet"
      onClose={onCancel}
      onSubmit={submit}
      title={<h2 id="prompt-pool-dialog-title">Edit prompt pool</h2>}
    >
      <FormField label="Name">
        <input
          className="form-input"
          maxLength={160}
          onChange={(event) => onChange({ ...value, name: event.target.value })}
          value={value.name}
        />
      </FormField>
      <FormField label="Description">
        <textarea
          className="form-input"
          maxLength={3000}
          onChange={(event) => onChange({ ...value, description: event.target.value })}
          value={value.description}
        />
      </FormField>
      <p className="muted">
        Contains {value.pool.variants.length} prompt variant(s).
      </p>
      <ModalActions>
        <Button disabled={isSaving} onClick={onCancel} variant="secondary">Cancel</Button>
        <Button disabled={!nameValid || isSaving} type="submit" variant="primary">
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </ModalActions>
    </Modal>
  )
}
